import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { PagedResult } from '../../Models/Http/HttpModels';

export type HttpError = Error & { status?: number; data?: unknown };

export default class HttpService {
  private axios: AxiosInstance;

  constructor(axiosInstance?: AxiosInstance, baseConfig?: AxiosRequestConfig) {
    if (axiosInstance) {
      this.axios = axiosInstance;
    } else {
      // lazy require to avoid importing ESM-axios during Jest collection
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { createAxiosWithInterceptors } = require('./interceptors/axiosInterceptors');
      this.axios = createAxiosWithInterceptors(baseConfig ?? { baseURL: process.env.REACT_APP_API_BASE_URL });
    }
  }

  async request<T = any>(config: AxiosRequestConfig): Promise<T> {
    try {
      const res = await this.axios
        .request(config);
      return res.data;
    } catch (err) {
      const error = err as any;
      const out: HttpError = new Error(error?.message || 'Network error');
      // Preserve normalized errors from interceptors when available.
      if (typeof error?.status !== 'undefined') {
        out.status = error.status;
      } else if (error && error.response) {
        out.status = error.response.status;
      }
      if (typeof error?.data !== 'undefined') {
        out.data = error.data;
      } else if (error && error.response) {
        out.data = error.response.data;
      }
      throw out;
    }
  }

  get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ method: 'GET', url, ...config });
  }

  list<T = any>(url: string, config?: AxiosRequestConfig): Promise<PagedResult<T> | T[]> {
    return this.request<PagedResult<T> | T[]>({ method: 'GET', url, ...config });
  }

  post<T = any, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ method: 'POST', url, data: body, ...config });
  }

  put<T = any, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ method: 'PUT', url, data: body, ...config });
  }

  patch<T = any, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ method: 'PATCH', url, data: body, ...config });
  }

  delete<T = any, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ method: 'DELETE', url, data: body, ...config });
  }
}
